import { defaultDataIdFromObject, InMemoryCache } from "@apollo/client";
import { revisionCacheTypePolicies } from "@creative-revision/api";
import {
  commentViewerOperationChecksFieldPolicy,
  noClaimConvergenceReadFieldPolicy,
  ordersFieldPolicy,
  paginationFieldPolicy,
  revisionCommentsFieldPolicy,
  unshiftFieldPolicy,
} from "./fieldPolicies";

export const createCache = () =>
  new InMemoryCache({
    typePolicies: {
      // The revision subgraph's `Revisions` object ({ count, nodes }) has no
      // identity and sits inline under `Deliverable.revisions` and
      // `DeliverableSpecification.revisions`; the type-level merge the api
      // package owns keeps a cached list's nodes when a count-only read (the
      // review panel's tree, the version list) lands on the same parent.
      ...revisionCacheTypePolicies,
      // A concept's slot list (`DeliverableSpecifications`, { meta, nodes }) has
      // no identity either and sits inline under `Concept.specifications`; the
      // workflow offers ask every concept for the count alone while the brief,
      // review and delivery reads ask the same field for the slots. Merged, a
      // write keeps the half it did not select, so neither read is left
      // incomplete by the other; a write carrying `nodes` still replaces them
      // whole, so a list that shrank server-side shrinks here. The trade, as for
      // `Revisions`: a merged object can carry a `meta` from an older read than
      // its `nodes`.
      DeliverableSpecifications: { merge: true },
      // A concept's stage (`WorkflowStage`) has no identity: the stage catalogue
      // is the tenant's, so its `code` cannot key a cache entity whose `name`
      // and `position` another tenant may spell differently. A write of the
      // stage the cache holds (the same `code`) merges, so `{ code }` written
      // over `{ code name position }` keeps the name and the position; a write
      // of another `code` is a move to another stage and replaces it, so no
      // field of the stage the concept left survives the move. A refetch's
      // write reaches this function with no cached stage, and so replaces it,
      // unless its read sets `refetchWritePolicy: "merge"`.
      WorkflowStage: {
        merge(existing, incoming, { mergeObjects, readField }) {
          const code = readField<string>("code", incoming);
          if (existing && code !== undefined && readField<string>("code", existing) === code) {
            return mergeObjects(existing, incoming);
          }
          return incoming;
        },
      },
      // A step's `key` is a globally unique UUID (unlike a stage's
      // tenant-scoped `code`), so every read of a step merges on one entity, and
      // a selection of a step without `key` fails at write time instead of
      // replacing the fields another read stored.
      WorkflowStep: { keyFields: ["key"] },
      Query: {
        fields: {
          clients: paginationFieldPolicy(["id", "q", "sort"]),
          copySuggestions: unshiftFieldPolicy([
            "audience",
            "audienceIntegrationKey",
            "description",
            "conceptIdea",
          ]),
          creators: paginationFieldPolicy(["$filter"]),
          deliverables: paginationFieldPolicy(["orderKey", "q"]),
          orders: ordersFieldPolicy,
        },
      },
      Project: {
        fields: {
          // `Project.deliverables` pages per project: the filter and the page
          // size are identity (differently sized windows — the tracker's
          // one-thumbnail read beside the delivery page's full walk — fence
          // into separate entries, as `limit` does for `orders`); the page
          // index stays out so pages of one list accumulate.
          deliverables: paginationFieldPolicy(["params", ["filter", "pagination", ["pageSize"]]]),
        },
      },
      UGCOrder: {
        fields: {
          deliverableLinks: {
            merge: false,
          },
        },
      },
      Revision: {
        fields: {
          // The fenced governed thread read: request-anchored (`atLeast`)
          // but ONE cache seam, with the request-scoped convergence
          // disclosure stripped before storage — see the policy's doc.
          comments: revisionCommentsFieldPolicy,
        },
      },
      RevisionComments: {
        fields: {
          // Cache reads synthesize the no-claim constant the merge never
          // stores — see the policy's doc.
          convergence: noClaimConvergenceReadFieldPolicy,
        },
      },
      Comment: {
        fields: {
          // The vOC fence twin: per-check convergence is stripped before
          // the checks store under `Comment:<id>` — see the policy's doc.
          viewerOperationChecks: commentViewerOperationChecksFieldPolicy,
        },
      },
      CommentOperationCheck: {
        fields: {
          // Cache reads synthesize the no-claim constant the merge never
          // stores — see the policy's doc.
          convergence: noClaimConvergenceReadFieldPolicy,
        },
      },
      // A format's dimension has no identity and is read with different
      // fields by the review panel and the review page; merged, neither read
      // drops the other's.
      SpecificationDimension: {
        merge: true,
      },
      DeliverableSpecification: {
        fields: {
          deliverables: {
            merge(existing = {}, incoming = {}) {
              return {
                ...existing,
                ...incoming,
                nodes: incoming.nodes ?? existing.nodes,
                meta: incoming.meta ?? existing.meta,
                metadata: incoming.metadata ?? existing.metadata,
              };
            },
          },
        },
      },
    },
    dataIdFromObject(object) {
      if (
        object.__typename === "CreatorAttributes" ||
        object.__typename === "Scopes" ||
        object.__typename === "GenerativeAIOptions"
      ) {
        // Scope queries can all be stored at the root level.
        return object.__typename;
      }
      if (object.__typename === "Language" && object.code) {
        return `${object.__typename}:${object.code}`;
      }
      // TODO: once all graphs are federated compliant, we can remove this and always use `key` or `integrationKey`
      if (object.__typename === "BrandProfile" && object.integrationKey) {
        return `${object.__typename}:${object.integrationKey}`;
      }
      if (object.__typename === "Project" && (object.key || object.integrationKey)) {
        return `${object.__typename}:${object.key || object.integrationKey}`;
      }
      if (object.__typename === "UGCDeliverable" && object.key) {
        return `${object.__typename}:${object.key}`;
      }
      if (object.__typename === "Audience" && object.key) {
        return `${object.__typename}:${object.key}`;
      }
      if (object.__typename === "Concept" && (object.key || object.integrationKey)) {
        return `${object.__typename}:${object.key || object.integrationKey}`;
      }
      if (object.__typename === "Advert" && object.integrationKey) {
        return `${object.__typename}:${object.integrationKey}`;
      }
      if (object.__typename === "Question" && object.key) {
        return `${object.__typename}:${object.key}`;
      }
      if (object.__typename === "ConceptBrief" && object.key) {
        return `${object.__typename}:${object.key}`;
      }
      if (object.__typename === "ClientAccount" && object.key) {
        return `${object.__typename}:${object.key}`;
      }
      if (object.__typename === "ClientGroup" && object.key) {
        return `${object.__typename}:${object.key}`;
      }
      if (object.__typename === "ClientBrand" && object.key) {
        return `${object.__typename}:${object.key}`;
      }
      if (object.__typename === "Client" && object.key) {
        return `${object.__typename}:${object.key}`;
      }
      if (object.__typename === "OrganizationalUnit" && object.key) {
        return `${object.__typename}:${object.key}`;
      }
      if (object.__typename === "GenerativeAIModel" && object.code) {
        return `${object.__typename}:${object.code}`;
      }
      if (object.__typename === "DeliveryTurnaround" && object.key) {
        return `${object.__typename}:${object.key}`;
      }
      if (object.__typename === "DeliverableSpecification" && object.key) {
        return `${object.__typename}:${object.key}`;
      }
      if (object.__typename === "DeliverableSpecificationFormat" && object.key && object.length) {
        // The same format can exist multiple times with different lengths and dimensions, this is because
        // the format is scoped to the specification which can be defined in briefing or deliverable upload.
        let dataId = `${object.__typename}:${object.key}:${object.length}`;
        const { dimension } = object;
        if (
          dimension &&
          typeof dimension === "object" &&
          "width" in dimension &&
          dimension.width != null &&
          "height" in dimension &&
          dimension.height != null
        ) {
          dataId = `${dataId}:${dimension.width}x${dimension.height}`;
          if ("unit" in dimension && dimension.unit != null) {
            dataId = `${dataId}_${dimension.unit}`;
          }
        }
        return dataId;
      }
      if (object.__typename === "Deliverable" && object.key) {
        return `${object.__typename}:${object.key}`;
      }
      if (object.__typename === "Order" && object.key) {
        return `${object.__typename}:${object.key}`;
      }
      if (object.__typename === "SpecificationConcept" && object.key) {
        return `${object.__typename}:${object.key}`;
      }
      // Reads with different variant selections share a concept (the review
      // panel's tree and the review page's concept): stored inline, the
      // variants list is an array the last read replaces whole, dropping the
      // other read's fields. Identified, each variant's fields merge.
      if (object.__typename === "Variant" && object.key) {
        return `${object.__typename}:${object.key}`;
      }
      if (object.__typename === "Platform" && object.code) {
        return `${object.__typename}:${object.code}`;
      }
      if (object.__typename === "File" && object.key) {
        return `${object.__typename}:${object.key}`;
      }
      if (object.__typename === "Market" && object.code) {
        return `${object.__typename}:${object.code}`;
      }

      // Fallback to default id handling
      return defaultDataIdFromObject(object);
    },
  });
